/*
 * The hero's simulated LiDAR corridor: a two-lane road through rolling, wooded hills, with
 * markings, light poles, signs, guardrail, an overpass and a few houses. World units are
 * metres; z runs along the road, x across it, y up. Everything is periodic in z (LOOP), so
 * the drive loops without a seam. The road's line and profile are rdvsystems.com's
 * ScanScene corridor. Surfaces are lit from one sun direction (baked into each point's
 * intensity), which is most of what makes the cloud read as 3D.
 */

export const LOOP = 1200;
export const LANE_OFF = 1.8; // centre of the right lane

// Point kinds; the shader colours by kind.
export const KIND = {
  road: 0,
  marking: 1,
  ground: 2,
  vegetation: 3,
  pole: 4,
  sign: 5,
  structure: 6,
  vehicle: 7,
  building: 8,
};

const OVERPASS_Z = 560;

export const wrap = (d) => ((d % LOOP) + LOOP) % LOOP;
export const around = (z, c) => {
  let d = wrap(z - c);
  if (d > LOOP / 2) d -= LOOP;
  return d;
};

export function centerX(z) {
  const a = (2 * Math.PI * z) / LOOP;
  return 30 * Math.sin(2 * a) + 12 * Math.sin(3 * a + 1.3);
}

export function roadY(z) {
  const bump = (c, h, w) => {
    const d = around(z, c);
    return h * Math.exp(-(d * d) / (2 * w * w));
  };
  return bump(230, 8, 52) + bump(780, 6, 42) + bump(1030, 2.5, 70);
}

/**
 * Ground height at lateral offset `off` from the centreline: shoulder, ditch, then banks
 * rising into rolling hills (periodic in z, like everything else).
 */
export function groundY(z, off) {
  const a = Math.abs(off);
  const base = roadY(z);
  if (a < 7.2) return base;
  if (a < 11) return base - (a - 7.2) * 0.34;
  const t = a - 11;
  const side = off < 0 ? 1.7 : 0;
  const k = (2 * Math.PI) / LOOP;
  const bank = t * 0.16 * (0.75 + 0.35 * Math.sin(z * 0.011 + off * 0.07));
  const hills =
    (1 - Math.exp(-t / 18)) *
    (5.5 * Math.sin(z * k * 7 + side) + 3 * Math.sin(z * k * 13 + off * 0.045 + side * 2));
  return base - 1.3 + bank + hills;
}

function rng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// The sun: from the upper left and a little ahead.
const SUN = (() => {
  const v = [-0.55, 0.75, 0.35];
  const l = Math.hypot(...v);
  return v.map((c) => c / l);
})();
const lambert = (nx, ny, nz) => Math.max(0, nx * SUN[0] + ny * SUN[1] + nz * SUN[2]);

/** Sun shading of the terrain at (z, off), from its local slope. */
function groundShade(z, off) {
  const e = 0.6;
  const gx = (groundY(z, off + e) - groundY(z, off - e)) / (2 * e);
  const gz = (groundY(z + e, off) - groundY(z - e, off)) / (2 * e);
  const l = Math.hypot(gx, 1, gz);
  return 0.35 + 0.85 * lambert(-gx / l, 1 / l, -gz / l);
}

/**
 * Builds the cloud. `density` scales the scattered points (road, ground, canopies); the
 * linear features (markings, rails, poles) stay whole so the road still reads on a phone.
 * Returns `pos` (x, y, z per point) and `meta` (intensity 0-1, kind per point).
 */
export function buildWorld(density = 1) {
  const rand = rng(20050817);
  const n = (k) => Math.max(1, Math.round(k * density));
  const pos = [];
  const meta = [];
  const push = (x, y, z, intensity, kind) => {
    pos.push(x, y, wrap(z));
    meta.push(Math.min(1, Math.max(0, intensity)), kind);
  };

  // Asphalt, between the shoulders.
  for (let i = 0; i < n(70000); i++) {
    const z = rand() * LOOP;
    const off = (rand() * 2 - 1) * 7.2;
    push(centerX(z) + off, roadY(z) + (rand() - 0.5) * 0.03, z, 0.25 + rand() * 0.25, KIND.road);
  }

  // Markings: solid edge lines and a dashed centre line, each a few points wide.
  for (let z = 0; z < LOOP; z += 0.32) {
    for (const off of [-3.6, 3.6]) {
      for (const w of [-0.06, 0.06]) {
        push(centerX(z) + off + w, roadY(z) + 0.01, z, 0.85 + rand() * 0.15, KIND.marking);
      }
    }
    if (z % 12 < 3.2) {
      push(centerX(z), roadY(z) + 0.01, z, 0.85 + rand() * 0.15, KIND.marking);
    }
  }

  // Terrain either side, out to ~75 m, shaded by its slope. Densest near the road, as a
  // vehicle-mounted scan is.
  for (let i = 0; i < n(85000); i++) {
    const z = rand() * LOOP;
    const side = rand() < 0.5 ? -1 : 1;
    const off = side * (7.4 + Math.pow(rand(), 1.7) * 70);
    const shade = groundShade(z, off);
    push(centerX(z) + off, groundY(z, off), z, (0.15 + rand() * 0.25) * shade * 1.4, KIND.ground);
  }

  // Trees: a trunk and a layered canopy, lit on the sun side and from above.
  const sunAz = Math.atan2(SUN[0], SUN[2]);
  for (let t = 0; t < 210; t++) {
    const z = rand() * LOOP;
    if (Math.abs(around(z, OVERPASS_Z)) < 18) continue;
    const side = rand() < 0.5 ? -1 : 1;
    const near = rand() < 0.3;
    const off = side * (near ? 9.5 + rand() * 6 : 15 + rand() * 55);
    const conifer = rand() < 0.45;
    const h = (conifer ? 10 : 7) + rand() * 9;
    const r = (conifer ? 1.6 : 2.2) + rand() * 2;
    const x0 = centerX(z) + off;
    const y0 = groundY(z, off);
    for (let k = 0; k < 14; k++) {
      push(x0 + (rand() - 0.5) * 0.25, y0 + rand() * h * 0.45, z + (rand() - 0.5) * 0.25, 0.25, KIND.vegetation);
    }
    for (let k = 0; k < n(170); k++) {
      const hh = 0.3 + rand() * 0.7;
      // Conifers taper to a point; broadleaf crowns are rounder.
      const profile = conifer ? 1 - (hh - 0.3) / 0.7 : Math.sin(((hh - 0.3) / 0.7) * Math.PI) * 0.8 + 0.25;
      const rr = r * profile * Math.sqrt(0.35 + rand() * 0.65);
      const ang = rand() * Math.PI * 2;
      const lit = 0.5 + 0.5 * Math.cos(ang - sunAz);
      push(
        x0 + Math.cos(ang) * rr,
        y0 + h * hh,
        z + Math.sin(ang) * rr,
        0.15 + 0.45 * hh + 0.3 * lit,
        KIND.vegetation
      );
    }
  }

  // Light poles on the right every 55 m, with an arm over the lane; every third one also
  // carries a sign, which scans bright: sign sheeting is retroreflective.
  for (let z = 20, i = 0; z < LOOP; z += 55, i++) {
    const off = 8.4;
    const x0 = centerX(z) + off;
    const y0 = groundY(z, off);
    for (let y = 0; y < 8; y += 0.12) push(x0, y0 + y, z, 0.55, KIND.pole);
    for (let a = 0; a < 3.2; a += 0.12) push(x0 - a, y0 + 8 + a * 0.08, z, 0.55, KIND.pole);
    if (i % 3 === 0) {
      const sz = z + 14;
      const sx = centerX(sz) + 7.8;
      const sy = groundY(sz, 7.8);
      for (let y = 0; y < 2.2; y += 0.12) push(sx, sy + y, sz, 0.55, KIND.pole);
      for (let u = -0.5; u <= 0.5; u += 0.09) {
        for (let v = 0; v <= 1; v += 0.09) push(sx + u, sy + 2.2 + v, sz, 0.95, KIND.sign);
      }
    }
  }

  // Guardrail along the curves and crests: two rails on posts, both sides.
  for (let z = 0; z < LOOP; z += 0.3) {
    if (Math.sin((z / LOOP) * Math.PI * 2 * 5 + 0.8) < 0.35) continue;
    if (Math.abs(around(z, OVERPASS_Z)) < 10) continue;
    for (const side of [-1, 1]) {
      const x = centerX(z) + side * 7.7;
      const y = roadY(z);
      push(x, y + 0.72, z, 0.7, KIND.structure);
      push(x, y + 0.5, z, 0.6, KIND.structure);
      if (z % 2 < 0.3) {
        for (let py = 0; py < 0.72; py += 0.12) push(x, y + py, z, 0.5, KIND.structure);
      }
    }
  }

  // An overpass: a deck across the road on four piers, with parapets. The underside is in
  // shadow, the top and the sun-facing faces are lit.
  {
    const zc = OVERPASS_Z;
    const cx = centerX(zc);
    const y0 = roadY(zc);
    const deck = y0 + 6.8;
    const half = 34;
    const width = 11;
    for (let i = 0; i < n(9000); i++) {
      const u = (rand() * 2 - 1) * half;
      const v = (rand() * 2 - 1) * (width / 2);
      const face = rand();
      if (face < 0.45) push(cx + u, deck, zc + v, 0.22, KIND.structure); // underside
      else if (face < 0.8) push(cx + u, deck + 1.1, zc + v, 0.75, KIND.structure); // top
      else {
        // Fascia on the approach side (lit) and the far side.
        const near = face < 0.9;
        push(cx + u, deck + rand() * 1.1, zc + (near ? -width / 2 : width / 2), near ? 0.8 : 0.35, KIND.structure);
      }
    }
    for (let u = -half; u <= half; u += 0.25) {
      for (const s of [-1, 1]) {
        for (let h = 0; h < 1.1; h += 0.25) push(cx + u, deck + 1.1 + h, zc + s * (width / 2), 0.7, KIND.structure);
      }
    }
    for (const px of [-23, -10.5, 10.5, 23]) {
      const gy = groundY(zc, px);
      for (let i = 0; i < n(700); i++) {
        const ang = rand() * Math.PI * 2;
        const lit = 0.3 + 0.6 * Math.max(0, Math.cos(ang - sunAz));
        push(cx + px + Math.cos(ang) * 0.6, gy + rand() * (deck - gy), zc + Math.sin(ang) * 0.6, lit, KIND.structure);
      }
    }
  }

  // Houses set back from the road: walls and a gabled roof, each face lit by the sun.
  for (let k = 0; k < 11; k++) {
    const z = (k / 11) * LOOP + rand() * 60;
    if (Math.abs(around(z, OVERPASS_Z)) < 40) continue;
    const side = k % 2 ? 1 : -1;
    const off = side * (24 + rand() * 26);
    const L = 9 + rand() * 5; // along z
    const W = 7 + rand() * 3; // across
    const H = 3 + rand() * 1.2;
    const R = 2.2 + rand() * 1.2;
    const x0 = centerX(z) + off;
    const y0 = groundY(z, off) - 0.2;
    const wall = (count, fn) => {
      for (let i = 0; i < n(count); i++) fn(rand(), rand());
    };
    // Front and back walls (facing z), side walls (facing x), with gable ends.
    for (const s of [-1, 1]) {
      const shade = 0.3 + 0.7 * lambert(0, 0, s);
      wall(260, (a, b) => {
        const u = (a - 0.5) * W;
        const top = H + R * (1 - Math.abs(u) / (W / 2));
        push(x0 + u, y0 + b * top, z + s * (L / 2), shade, KIND.building);
      });
      const shadeX = 0.3 + 0.7 * lambert(s, 0, 0);
      wall(320, (a, b) => push(x0 + s * (W / 2), y0 + b * H, z + (a - 0.5) * L, shadeX, KIND.building));
      // Roof slope on this side.
      const rl = Math.hypot(W / 2, R);
      const roofShade = 0.3 + 0.7 * lambert((s * R) / rl, W / 2 / rl, 0);
      wall(420, (a, b) => {
        const u = b * (W / 2);
        push(x0 + s * u, y0 + H + R * (1 - u / (W / 2)), z + (a - 0.5) * L, roofShade, KIND.building);
      });
    }
  }

  return {
    pos: new Float32Array(pos),
    meta: new Float32Array(meta),
    count: pos.length / 3,
  };
}

// The scanning vehicle, in its own frame: `along` forward, `across` to the right, `up`.
// A sedan with a roof-mounted scanner, sampled the way a scan sees one: solid panels,
// sparse glass, dark tyres.
const CAR = (() => {
  const rand = rng(7);
  const pts = []; // along, across, up, intensity
  const put = (a, s, u, i) => pts.push(a, s, u, i);
  const L = 2.35;
  const W = 0.92;
  const BELT = 0.95;
  // Roof line front to back: bumper, hood, windscreen, roof, rear window, boot.
  const top = (a) => {
    if (a > 2.2) return 0.75 + (2.35 - a) * 0.8;
    if (a > 1.0) return 0.95 - (a - 1.0) * 0.08;
    if (a > 0.25) return 1.45 - ((a - 0.25) / 0.75) * 0.5;
    if (a > -1.0) return 1.45 + 0.03 * Math.cos(((a + 0.375) / 1.25) * Math.PI);
    if (a > -1.75) return 1.45 - ((-1.0 - a) / 0.75) * 0.47;
    return 0.98;
  };
  const glass = (a) => a > -1.75 && a < 1.0;
  // Cabin sides lean in toward the roof.
  const halfWidth = (a, u) => (u <= BELT ? W : W - ((u - BELT) / 0.5) * 0.2);

  // Lower body sides, below the belt line.
  for (let i = 0; i < 700; i++) {
    const a = (rand() * 2 - 1) * L;
    const u = 0.3 + rand() * (Math.min(BELT, top(a)) - 0.3);
    const s = rand() < 0.5 ? -1 : 1;
    // Wheel arches: leave the panels open over the wheels.
    if (Math.abs(Math.abs(a) - 1.45) < 0.42 && u < 0.72) continue;
    put(a, s * W, u, s > 0 ? 0.55 : 0.75);
  }
  // Side windows: glass returns few points.
  for (let i = 0; i < 160; i++) {
    const a = -1.7 + rand() * 2.65;
    const u = BELT + rand() * (top(a) - BELT);
    const s = rand() < 0.5 ? -1 : 1;
    put(a, s * halfWidth(a, u), u, 0.22);
  }
  // Hood, roof and boot; the windscreen and rear window sparse, like the side glass.
  for (let i = 0; i < 900; i++) {
    const a = (rand() * 2 - 1) * L;
    const u = top(a);
    const isGlass = glass(a) && (a > 0.25 || a < -1.0);
    if (isGlass && rand() < 0.75) continue;
    const s = (rand() * 2 - 1) * halfWidth(a, u) * 0.96;
    put(a, s, u, isGlass ? 0.25 : 0.9);
  }
  // Front and back faces.
  for (let i = 0; i < 260; i++) {
    const end = rand() < 0.5 ? -1 : 1;
    const a = end * L;
    const u = 0.3 + rand() * (top(a) - 0.3);
    put(a, (rand() * 2 - 1) * W, u, end > 0 ? 0.8 : 0.6);
  }
  // Tyres: a dark disc each, rim bright.
  for (const wa of [-1.45, 1.45]) {
    for (const s of [-1, 1]) {
      for (let i = 0; i < 70; i++) {
        const ang = rand() * Math.PI * 2;
        const r = 0.34 * Math.sqrt(rand());
        put(wa + Math.cos(ang) * r, s * (W - 0.02), 0.34 + Math.sin(ang) * r, r < 0.2 ? 0.55 : 0.18);
      }
    }
  }
  // The scanner: a short cylinder on a mast at the back of the roof, its cap the brightest point.
  for (let u = 1.48; u < 1.62; u += 0.035) put(-0.55, 0, u, 0.6);
  for (let k = 0; k < 40; k++) {
    const ang = (k / 40) * Math.PI * 2;
    const u = 1.62 + (k % 4) * 0.05;
    put(-0.55 + Math.cos(ang) * 0.13, Math.sin(ang) * 0.13, u, 0.75);
  }
  for (let k = 0; k < 6; k++) put(-0.55, 0, 1.82 + k * 0.01, 1);
  return new Float32Array(pts);
})();
export const SCANNER_UP = 1.82; // m, the scanner head above the road

const carPos = new Float32Array((CAR.length / 4) * 3);
const carMeta = new Float32Array((CAR.length / 4) * 2);

/**
 * The scanning vehicle at road position `z` in the right lane, turned and pitched with the
 * road. Reuses its arrays: the caller uploads them each frame.
 */
export function placeVehicle(z) {
  const x0 = centerX(z) + LANE_OFF;
  const y0 = roadY(z);
  const hx = centerX(z + 1) - centerX(z - 1);
  const len = Math.hypot(hx, 2);
  const fx = hx / len;
  const fz = 2 / len;
  const pitch = (roadY(z + 1.5) - roadY(z - 1.5)) / 3;
  const count = CAR.length / 4;
  for (let i = 0; i < count; i++) {
    const a = CAR[i * 4];
    const s = CAR[i * 4 + 1];
    const u = CAR[i * 4 + 2];
    carPos[i * 3] = x0 + fx * a + fz * s;
    carPos[i * 3 + 1] = y0 + u + a * pitch;
    carPos[i * 3 + 2] = wrap(z + fz * a - fx * s);
    carMeta[i * 2] = CAR[i * 4 + 3];
    carMeta[i * 2 + 1] = KIND.vehicle;
  }
  return { pos: carPos, meta: carMeta, count };
}
